<template>
  <div class="auth-form">
    <div class="auth-form__group">
      <div class="auth-form__group--top">
        <form-input-phone
          v-if="variant === 'phone'"
          v-model:value="authForm.phone"
          :placeholder="getContent(fieldsSettings, defaultLocaleFieldsSettings, `fieldsControls.phone.placeholder`)"
          is-required
          :hint="authSetError('phone')"
          :countryCode="countryOrigin"
          hideValidationMessage
          @blur="onBlur('phone', 'invalidPhoneNumberFormat')"
        />

        <form-input-text
          v-if="isEmail"
          v-model:value="authForm.email"
          label=""
          :placeholder="getContent(currentLocaleData, defaultLocaleData, 'enterEmailAddress')"
          name="email"
          autocomplete="off"
          is-required
          :hint="authSetError('email')"
          :class="'auth-field'"
          hideValidationMessage
          @blur="onBlur('email', 'invalidEmailFormat')"
        />

        <form-input-password
          v-model:value="authForm.password"
          name="newPassword"
          :placeholder="getContent(currentLocaleData, defaultLocaleData, 'enterPassword')"
          is-required
          label=""
          class="auth-field"
          :showPassProgress="type !== 'login'"
          :hint="authSetError('password')"
          hideValidationMessage
          @submit="handleSubmit"
          @blur="onBlur('password', 'passwordTooShort')"
        />

        <div v-if="isLogin" class="auth-form__forgot-password" @click="openForgotPassDialog">
          {{ getContent(currentLocaleData, defaultLocaleData, 'forgotPassword') }}
        </div>

        <div v-else>
          <div class="auth-form__bonus-label">
            {{ getContent(fieldsSettings, defaultLocaleFieldsSettings, 'fieldsControls.bonusCode.label') }}
          </div>

          <form-input-text
            v-model:value="authForm.bonusCode"
            label=""
            :placeholder="
              getContent(fieldsSettings, defaultLocaleFieldsSettings, 'fieldsControls.bonusCode.placeholder')
            "
            name="bonus"
            autocomplete="off"
            :class="'auth-field'"
          />
        </div>
      </div>

      <div class="auth-form__group--bottom">
        <form-input-checkbox
          v-if="type === 'registration'"
          class="auth-form__tc"
          v-model="authForm.agreements"
          :hint="authSetError('agreements')"
        >
          <span class="auth-form__tc-text">
            {{ getContent(currentLocaleData, defaultLocaleData, 'consent') }}

            <NuxtLink :to="bonusTermsLink" target="_blank">
              {{ getContent(currentLocaleData, defaultLocaleData, 'termsCondition') }}
            </NuxtLink>
          </span>
        </form-input-checkbox>

        <div class="auth-form__buttons">
          <button-base
            type="primary"
            size="md"
            class="auth-form__register auth-form__btn"
            :is-disabled="btnDisabled"
            :isProcessing="isLockedAsyncButton"
            @click="handleSubmit"
          >
            {{ buttonSubmitText[type] }}
          </button-base>

          <atomic-socials />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ModalName } from '@skeleton/consts/modals';
  import type { IModalsContent } from '~/types';

  const props = defineProps<{
    type: 'registration' | 'login';
    variant: 'email' | 'phone';
    options: ICountry[];
    countryOrigin: string;
    currentLocaleData: Maybe<IModalsContent['authorization']>;
    defaultLocaleData: Maybe<IModalsContent['authorization']>;
  }>();

  const { getContent, localizePath } = useProjectMethods();
  const { getToken, preloadScript } = useRecaptcha();

  const { settingsConstants } = storeToRefs(useGlobalStore());
  const { logIn, registration, sendPixelEventToAnalytics } = useProfileStore();

  const { defaultLocaleAlertsData, alertsData, fieldsSettings, defaultLocaleFieldsSettings } =
    storeToRefs(useGlobalStore());

  const { showAlert } = useLayoutStore();
  const { closeModalSync, openModal } = useModalStore();

  interface ICountry {
    name: string;
    code: string;
    icon: string;
    phonePrefix: string;
  }

  const passwordMinLengthByType = {
    login: 6,
    registration: 8,
  };

  const authForm = reactive({
    agreements: false,
    phone: '',
    password: '',
    bonusCode: '',
    email: '',
  });

  const formValidationRules = ref({});

  const { getFormRules, createValidationRules } = useProjectMethods();
  const authFormRules = computed(() => getFormRules(formValidationRules.value));

  const { v$: authV$, setError: authSetError } = useFormValidation(authFormRules, authForm);

  watchEffect(() => {
    const validationRules = [
      { name: props.variant, isRequired: true },
      { name: 'password', isRequired: true },
    ];

    if (props.type === 'registration') {
      validationRules.push({ name: 'agreements', isRequired: true });
    }

    formValidationRules.value = createValidationRules(validationRules, true);

    const rules = createValidationRules(validationRules, true);

    const passwordRules = rules.password;
    if (Array.isArray(passwordRules)) {
      const minRule = passwordRules.find(rule => rule.rule === 'min');

      if (minRule) {
        minRule.arguments = passwordMinLengthByType[props.type];
      }
    }

    formValidationRules.value = rules;
  });

  const buttonSubmitText = computed(() => ({
    registration: getContent(props.currentLocaleData, props.defaultLocaleData, 'register'),
    login: getContent(props.currentLocaleData, props.defaultLocaleData, 'login'),
  }));

  const bonusTermsLink = computed(() => localizePath('/static/bonus-terms'));

  const isLogin = computed(() => props.type === 'login');

  const isEmail = computed(() => props.variant === 'email');

  const btnDisabled = computed(() => {
    if (isLockedAsyncButton.value) return true;

    const v$ = authV$.value;
    const fields: (keyof typeof authForm)[] = isEmail.value ? ['email', 'password'] : ['phone', 'password'];

    return fields.some(field => !authForm[field] || v$[field]?.$invalid);
  });

  const onBlur = async (fieldName: string, fieldContent: string) => {
    const valid = await authV$.value[fieldName]?.$validate();

    if (!valid) {
      authV$.value[fieldName]?.$touch();

      let content = getContent(alertsData.value?.profile, defaultLocaleAlertsData.value?.profile, fieldContent);

      if (fieldContent === 'passwordTooShort' && content?.description) {
        content = {
          ...content,
          description: content.description.replace('{count}', String(passwordMinLengthByType[props.type])),
        };
      }

      showAlert(content, 'error');
    }
  };

  const handleSubmit = () => {
    if (isLogin.value) {
      login();
    } else {
      register();
    }
  };

  const openForgotPassDialog = (): void => {
    closeSignInDialog();
    openModal(ModalName.FORGOT_PASS);
  };

  const closeSignInDialog = (): void => {
    closeModalSync(ModalName.SIGN_IN);
    closeModalSync(ModalName.SIGN_UP);
  };

  const isLockedAsyncButton = ref(false);

  const register = async () => {
    const validFormData = await authV$.value.$validate();
    if (!validFormData) return;

    try {
      isLockedAsyncButton.value = true;
      const recaptchaToken = await getToken('login');

      const registrationData = {
        password: authForm.password,
        country: props.countryOrigin,
        refId: authForm.bonusCode,
        bonusCode: authForm.bonusCode,
        agreements: authForm.agreements,
        recaptchaToken,
      };

      if (isEmail.value) {
        registrationData.email = authForm.email;
      } else {
        registrationData.phone = authForm.phone;
      }

      await registration(
        registrationData,
        isEmail.value ? settingsConstants?.value?.player?.auth?.requireEmailVerification : false
      );

      closeSignInDialog();

      if (isEmail.value) {
        await openModal(ModalName.CONFIRM_EMAIL);
      }
    } catch (error: any) {
      const errorResponse = [401, 422, 403];

      if (errorResponse.includes(error.response?.status)) {
        showAlert(
          getContent(alertsData.value?.global, defaultLocaleAlertsData.value?.global, 'somethingWrong'),
          'error'
        );
      }

      if (error.data?.error?.code === 10025) {
        closeModalSync(ModalName.SIGN_IN);
        closeModalSync(ModalName.SIGN_UP);
        await openModal(ModalName.GEO_POPUP);
      }

      useEvent('pixelEvent', 'regnotfin');
      sendPixelEventToAnalytics('regnotfin');
    } finally {
      isLockedAsyncButton.value = false;
    }
  };

  const login = async () => {
    const validFormData = await authV$.value.$validate();

    if (!validFormData) return;

    try {
      isLockedAsyncButton.value = true;

      const recaptchaToken = await getToken('login');

      await logIn({
        login: isEmail.value ? authForm.email : authForm.phone,
        password: authForm.password,
        recaptchaToken,
      });
      closeSignInDialog();
    } catch (error: any) {
      const errorResponse = [401, 422, 403];

      if (errorResponse.includes(error.response?.status)) {
        const key = isEmail.value ? 'incorrectEmailPassword' : 'incorrectPhoneNumberPassword';
        showAlert(getContent(alertsData.value?.profile, defaultLocaleAlertsData.value?.profile, key), 'error');
      }
    } finally {
      isLockedAsyncButton.value = false;
    }
  };

  onMounted(async () => {
    await preloadScript();
  });
</script>

<style src="~/assets/styles/components/form/auth.scss" lang="scss" />
